import { recordIsUnconfirmedEntryId } from "../contracts/business-graph-entry-state.js";
import { BusinessGraphRichnessScoreV1Schema, DEFAULT_RICHNESS_GATE_POLICY, getMinimumRichnessForPackage, richnessScoreToBand, } from "../contracts/business-graph-richness.js";
import { ROADMAP_BLOCKED_SITE_PACKAGE_IDS, } from "../contracts/site-package.js";
import { buildEligibilityBlockedResponse, evaluateBusinessProfileReadiness, isSitePackageEligible, } from "./evaluate-business-profile-readiness.js";
import { evaluatePackageRequiredFactGaps, packageMeetsRequiredFactContract, } from "../site-package/business-graph-package-requirements.js";
function recordIsUnconfirmed(id) {
    return recordIsUnconfirmedEntryId(id);
}
function graphHasIntakeUnverifiedMarkers(graph) {
    return (graph.services.some((s) => recordIsUnconfirmed(s.id)) ||
        graph.operations.serviceAreas.some((a) => a.id != null && recordIsUnconfirmed(a.id)) ||
        graph.evidence.records.some((r) => recordIsUnconfirmed(r.id)) ||
        graph.trust.license.some((l) => recordIsUnconfirmed(l.id)) ||
        graph.trust.insurance.some((i) => recordIsUnconfirmed(i.id)) ||
        graph.trust.certifications.some((c) => recordIsUnconfirmed(c.id)));
}
function hasContact(graph) {
    return Boolean(graph.business.primaryPhone?.trim() || graph.business.primaryEmail?.trim());
}
function hasServiceArea(graph) {
    return graph.operations.serviceAreas.some((a) => a.name.trim().length > 0);
}
function hasPrimaryService(graph) {
    return graph.services.some((s) => s.name.trim().length > 0);
}
function hasMultipleServices(graph) {
    return graph.services.length >= 2;
}
function hasConfirmedProof(graph) {
    const confirmedLicense = graph.trust.license.some((l) => !recordIsUnconfirmed(l.id));
    const confirmedInsurance = graph.trust.insurance.some((i) => !recordIsUnconfirmed(i.id));
    const confirmedCerts = graph.trust.certifications.some((c) => !recordIsUnconfirmed(c.id));
    const confirmedEvidence = graph.evidence.records.some((r) => !recordIsUnconfirmed(r.id) && r.evidenceType.trim().length > 0);
    return confirmedLicense || confirmedInsurance || confirmedCerts || confirmedEvidence;
}
function hasConfirmedMedia(graph) {
    return graph.assets.some((a) => !recordIsUnconfirmed(a.id));
}
function hasSupportedClaims(graph) {
    if (graph.trust.claims.length === 0)
        return false;
    const hasLicenseProof = graph.trust.license.some((l) => !recordIsUnconfirmed(l.id)) ||
        graph.trust.certifications.some((c) => !recordIsUnconfirmed(c.id));
    const hasInsuranceProof = graph.trust.insurance.some((i) => !recordIsUnconfirmed(i.id));
    return graph.trust.claims.every((claim) => {
        const id = claim.id.toLowerCase();
        if (id.includes("licensed") || id.includes("certified"))
            return hasLicenseProof;
        if (id.includes("insured"))
            return hasInsuranceProof;
        return true;
    });
}
function graphUsesUnconfirmedEvidence(graph) {
    return (graph.evidence.records.some((r) => recordIsUnconfirmed(r.id)) ||
        graph.trust.license.some((l) => recordIsUnconfirmed(l.id)) ||
        graph.trust.insurance.some((i) => recordIsUnconfirmed(i.id)) ||
        graph.trust.certifications.some((c) => recordIsUnconfirmed(c.id)) ||
        graph.assets.some((a) => recordIsUnconfirmed(a.id)));
}
function countSourceBreakdown(graph) {
    let confirmedActiveFields = 0;
    let agencyEnteredFields = 0;
    let intakeDerivedFields = 0;
    let unknownOrUnconfirmedFields = 0;
    const intakeUnverifiedGraph = graphHasIntakeUnverifiedMarkers(graph);
    if (graph.business.businessName.trim()) {
        if (intakeUnverifiedGraph) {
            intakeDerivedFields += 1;
        }
        else {
            confirmedActiveFields += 1;
        }
    }
    if (hasContact(graph)) {
        if (intakeUnverifiedGraph) {
            intakeDerivedFields += 1;
        }
        else {
            confirmedActiveFields += 1;
        }
    }
    if (hasServiceArea(graph)) {
        if (intakeUnverifiedGraph) {
            intakeDerivedFields += 1;
        }
        else {
            confirmedActiveFields += 1;
        }
    }
    for (const service of graph.services) {
        if (recordIsUnconfirmed(service.id)) {
            unknownOrUnconfirmedFields += 1;
        }
        else if (service.id.toLowerCase().includes("intake")) {
            intakeDerivedFields += 1;
        }
        else {
            agencyEnteredFields += 1;
        }
    }
    for (const record of graph.evidence.records) {
        if (recordIsUnconfirmed(record.id)) {
            unknownOrUnconfirmedFields += 1;
        }
        else if (record.id.toLowerCase().includes("intake")) {
            intakeDerivedFields += 1;
        }
        else {
            confirmedActiveFields += 1;
        }
    }
    return {
        confirmedActiveFields,
        agencyEnteredFields,
        intakeDerivedFields,
        unknownOrUnconfirmedFields,
    };
}
function computeRichnessScore(graph) {
    let score = 0;
    if (graph.business.businessName.trim())
        score += 10;
    if (hasContact(graph))
        score += 15;
    if (hasServiceArea(graph))
        score += 15;
    if (hasPrimaryService(graph))
        score += 15;
    if (hasMultipleServices(graph))
        score += 10;
    if (hasConfirmedProof(graph))
        score += 20;
    if (hasConfirmedMedia(graph))
        score += 10;
    if (hasSupportedClaims(graph))
        score += 5;
    return Math.min(100, score);
}
export function evaluateBusinessGraphRichness(graph, policy = DEFAULT_RICHNESS_GATE_POLICY) {
    const score = computeRichnessScore(graph);
    const unconfirmedEvidenceUsed = graphUsesUnconfirmedEvidence(graph);
    return BusinessGraphRichnessScoreV1Schema.parse({
        schemaVersion: "business-graph-richness-score.v1.0",
        score,
        band: richnessScoreToBand(score),
        sourceBreakdown: countSourceBreakdown(graph),
        dimensionFlags: {
            hasContact: hasContact(graph),
            hasServiceArea: hasServiceArea(graph),
            hasPrimaryService: hasPrimaryService(graph),
            hasMultipleServices: hasMultipleServices(graph),
            hasProofOrCredentials: hasConfirmedProof(graph),
            hasMedia: hasConfirmedMedia(graph),
            hasSupportedClaims: hasSupportedClaims(graph),
            unconfirmedEvidenceUsed,
        },
        policyVersion: policy.policyVersion,
        evaluatedAt: new Date().toISOString(),
        evaluatedFromBusinessGraphSnapshotHash: graph.snapshotHash,
        confirmedFieldsOnly: true,
    });
}
export function isPackageAllowedByRichness(richness, requestedPackage, policy = DEFAULT_RICHNESS_GATE_POLICY, graph) {
    if (graph && !packageMeetsRequiredFactContract(graph, requestedPackage)) {
        return false;
    }
    const minimum = getMinimumRichnessForPackage(requestedPackage, policy);
    if (richness.score < minimum) {
        return false;
    }
    const standardFiveMinimum = getMinimumRichnessForPackage("package-standard-5", policy);
    if (richness.dimensionFlags.unconfirmedEvidenceUsed &&
        minimum >= standardFiveMinimum) {
        return false;
    }
    return true;
}
export function isGrowthPackageEligibleByReadiness(readiness) {
    return (readiness.readinessLevel !== "NEEDS_BASICS" &&
        readiness.hardBlockers.length === 0);
}
export function isGenerationPackageEligible(graph, requestedPackage, policy = DEFAULT_RICHNESS_GATE_POLICY) {
    if (!packageMeetsRequiredFactContract(graph, requestedPackage)) {
        return false;
    }
    const readiness = evaluateBusinessProfileReadiness(graph, { forPackage: requestedPackage });
    const richness = evaluateBusinessGraphRichness(graph, policy);
    if (ROADMAP_BLOCKED_SITE_PACKAGE_IDS.includes(requestedPackage)) {
        if (!isGrowthPackageEligibleByReadiness(readiness)) {
            return false;
        }
        return isPackageAllowedByRichness(richness, requestedPackage, policy, graph);
    }
    if (!isSitePackageEligible(readiness, requestedPackage, graph)) {
        return false;
    }
    return isPackageAllowedByRichness(richness, requestedPackage, policy, graph);
}
export function buildRichnessEligibilityBlockedResponse(readiness, richness, requestedPackage, policy = DEFAULT_RICHNESS_GATE_POLICY, graph) {
    const base = buildEligibilityBlockedResponse(readiness, requestedPackage);
    const minimumRichnessRequired = getMinimumRichnessForPackage(requestedPackage, policy);
    let richnessBlockReason;
    let packageRequiredFieldBlockReason;
    if (graph) {
        const requiredFactGaps = evaluatePackageRequiredFactGaps(graph, requestedPackage);
        if (requiredFactGaps.length > 0) {
            packageRequiredFieldBlockReason = requiredFactGaps
                .map((gap) => `${gap.factPath}: ${gap.message}`)
                .join("; ");
        }
    }
    const standardFiveMinimum = getMinimumRichnessForPackage("package-standard-5", policy);
    if (richness.score < minimumRichnessRequired) {
        richnessBlockReason = `Business profile richness (${richness.score}) is below minimum (${minimumRichnessRequired}) for ${requestedPackage}`;
    }
    else if (richness.dimensionFlags.unconfirmedEvidenceUsed &&
        minimumRichnessRequired >= standardFiveMinimum) {
        richnessBlockReason =
            "Unconfirmed import or scan evidence cannot unlock higher packages until agency confirmation";
    }
    return {
        ...base,
        richnessScore: richness.score,
        minimumRichnessRequired,
        richnessBlockReason,
        packageRequiredFieldBlockReason,
    };
}
//# sourceMappingURL=evaluate-business-graph-richness.js.map